
export const normalizeOrderType = (type = "") => {
  const value = String(type).toLowerCase().trim();

  if (["home_delivery", "homedelivery", "home delivery"].includes(value)) {
    return "home_delivery";
  }

  if (["dining", "dinein", "dine-in"].includes(value)) {
    return "dining";
  }

  if (["takeaway", "takeway", "take away"].includes(value)) {
    return "takeaway";
  }

  if (["room_order", "room order", "roomorder"].includes(value)) {
    return "room_order";
  }

  return value;
};

export const getOrderTypeLabel = (type = "") => {
  const normalized = normalizeOrderType(type);

  if (normalized === "home_delivery") return "HOME DELIVERY";
  if (normalized === "dining") return "DINE IN";
  if (normalized === "takeaway") return "TAKEAWAY";
  if (normalized === "room_order") return "ROOM ORDER";

  return String(type || "N/A").toUpperCase();
};

export const today = () => {
  const date = new Date();

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

export const cleanName = (name) => {
  if (!name) return "";

  return name.replace(/\[Emoji:[^\]]+\]/g, "").trim();
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "";

  return dateStr.slice(0, 16);
};

export const safeAmount = (value) => {
  const amount = Number(value || 0);

  if (!Number.isFinite(amount)) return 0;

  return Math.max(0, amount);
};

export const isOrderPaidOrSettled = (order) => {
  if (!order) return false;

  const status = String(order.status || "").toLowerCase();
  const paymentStatus = String(order.payment_status || "").toLowerCase();

  return (
    status === "completed" ||
    status === "closed" ||
    paymentStatus === "paid"
  );
};

export const hasPreviousPayment = (order) => {
  if (!order) return false;

  const paidAmount = Number(
    order?.paid_amount ||
      order?.pay_amount ||
      order?.received_amount ||
      0,
  );

  const paymentStatus = String(order?.payment_status || "")
    .trim()
    .toLowerCase();

  return paidAmount > 0 || paymentStatus === "partially_paid";
};

const getStoredDeliveryCharge = (orderId) => {
  try {
    if (!orderId) return 0;

    const raw = sessionStorage.getItem(`pos_delivery_charge_${orderId}`);

    if (!raw) return 0;

    const parsed = JSON.parse(raw);

    return safeAmount(parsed?.deliveryAmount);
  } catch (error) {
    console.error("Delivery charge cache parse error:", error);
    return 0;
  }
};

const getApiDeliveryCharge = (order) => {
  return safeAmount(
    order?.deliveryAmount ||
      order?.delivery_amount ||
      order?.delivery_fee ||
      order?.deliveryCharges ||
      order?.delivery_charge ||
      0,
  );
};

export const getDeliveryCharge = (order) => {
  const apiDeliveryCharge = getApiDeliveryCharge(order);

  if (apiDeliveryCharge > 0) {
    return apiDeliveryCharge;
  }

  const storedDeliveryCharge = getStoredDeliveryCharge(
    order?.orderid || order?.orderId,
  );

  if (storedDeliveryCharge > 0) {
    return storedDeliveryCharge;
  }

  const dueAmount = Number(order?.due_amount || 0);

  if (
    normalizeOrderType(order?.orderType) === "home_delivery" &&
    dueAmount < 0
  ) {
    return Math.round(Math.abs(dueAmount));
  }

  return 0;
};

const getOrderGstType = (order) => {
  const orderValue = String(
    order?.gst_type ||
      order?.gstType ||
      order?.tax_type ||
      order?.taxType ||
      "",
  )
    .toLowerCase()
    .trim();

  if (["incl", "inclusive", "included"].includes(orderValue)) {
    return "incl";
  }

  if (["excl", "exclusive", "excluded"].includes(orderValue)) {
    return "excl";
  }

  try {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");

    const userID =
      user?.userid ||
      user?.userId ||
      user?.fk_userid ||
      user?.id ||
      "";

    const raw = sessionStorage.getItem(`pos_org_tax_config_${userID}`);
    const storedTaxConfig = raw ? JSON.parse(raw) : {};

    const storedValue = String(
      storedTaxConfig?.gstType ||
        storedTaxConfig?.gst_type ||
        "",
    )
      .toLowerCase()
      .trim();

    if (["incl", "inclusive", "included"].includes(storedValue)) {
      return "incl";
    }

    if (["excl", "exclusive", "excluded"].includes(storedValue)) {
      return "excl";
    }
  } catch (error) {
    console.error("GST type parse error:", error);
  }

  return "incl";
};

const getHardcodedTaxAmounts = (amount) => {
  const taxableAmount = safeAmount(amount);
  const totalIncludedTax = taxableAmount - taxableAmount / 1.05;

  return {
    cgst: totalIncludedTax / 2,
    sgst: totalIncludedTax / 2,
  };
};

export const getDisplayOrderAmount = (order) => {
  const delivery = getDeliveryCharge(order);

  const subtotal = safeAmount(
    order?.subtotal || order?.totalOrderPrice,
  );

  const discount = safeAmount(order?.discount_amount);

  const taxableAmount = Math.max(subtotal - discount, 0);

  const gstType = getOrderGstType(order);
  const taxAmounts = getHardcodedTaxAmounts(taxableAmount);

  if (gstType === "incl") {
    return taxableAmount + delivery;
  }

  return (
    taxableAmount +
    taxAmounts.cgst +
    taxAmounts.sgst +
    delivery
  );
};

export const getDisplayDueAmount = (order) => {
  const apiDueRaw = Number(order?.due_amount || 0);

  const paymentStatus = String(
    order?.payment_status || "",
  ).toLowerCase();

  const status = String(order?.status || "").toLowerCase();

  if (
    paymentStatus === "paid" ||
    status === "completed" ||
    status === "closed"
  ) {
    return 0;
  }

  if (apiDueRaw > 0) return apiDueRaw;
  if (apiDueRaw < 0) return 0;

  const displayTotal = getDisplayOrderAmount(order);

  const paid = safeAmount(
    order?.paid_amount || order?.pay_amount,
  );

  return Math.max(displayTotal - paid, 0);
};

export const getDefaultDrawerState = () => ({
  isOpen: false,
  sessionId: "",
  sessionCode: "",
  drawerStatus: "CLOSED",
  openingCash: "0",
  currentBalance: "0",
  cashSales: "0",
  cardSales: "0",
  upiSales: "0",
  cashIn: "0",
  cashOut: "0",
  refund: "0",
  openedAmount: "",
  closedAmount: "",
  openedAt: "",
  closedAt: "",
  expectedCash: "",
  actualCash: "",
  difference: "",
});

export const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

export const parseUserRole = (userRole = "") => {
  const [fkuserid = ""] = String(userRole || "").split("|");

  return {
    fkuserid: fkuserid.trim(),
  };
};

export const getPosPinSessionKey = (sessionUser = {}) => {
  const { fkuserid } = parseUserRole(sessionUser?.userRole);

  return `pos_pin_verified_${sessionUser?.userid || "unknown"}_${
    fkuserid || "unknown"
  }`;
};

export const getDrawerStorageKey = (sessionUser = {}) => {
  const { fkuserid } = parseUserRole(sessionUser?.userRole);

  return `pos_drawer_state_${sessionUser?.userid || "unknown"}_${
    fkuserid || "unknown"
  }`;
};

export const normalizePosButtonValue = (value = "") => {
  const normalizedValue = String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

  if (
    ["takeaway", "takeway", "take_away", "takwaway"].includes(
      normalizedValue,
    )
  ) {
    return "takeaway";
  }

  if (
    ["home_delivery", "homedelivery", "home__delivery"].includes(
      normalizedValue,
    )
  ) {
    return "homedelivery";
  }

  if (["dining", "dinein", "dine_in"].includes(normalizedValue)) {
    return "dinein";
  }

  if (
    ["room_order", "roomorder", "room__order"].includes(
      normalizedValue,
    )
  ) {
    return "room_order";
  }

  return normalizedValue;
};

export const getUserDrawerIds = (user = {}) => {
  return {
    terminalId: String(user?.terminalId || "").trim(),
    drawerId: String(user?.drawerId || "").trim(),
  };
};

export const getCatalogStorageKey = (userid = "") => {
  return `pos_catalog_cache_${userid}`;
};

export const normalizeTaxConfig = (orgInfo = {}) => {
  const totalTaxRate = Number(
    orgInfo?.tax_gst || orgInfo?.gst || 0,
  );

  return {
    gstEnabled: totalTaxRate > 0 ? "Y" : "N",
    gstType: orgInfo?.gst_type || "incl",
    totalTaxRate,
    cgstRate: totalTaxRate / 2,
    sgstRate: totalTaxRate / 2,

    organizationName: orgInfo?.organization_name || "",
    gstNumber: orgInfo?.gst || "",
    paymentGateway: orgInfo?.payment_gateway || "",
  };
};

export const getOrgTaxStorageKey = (userid = "") => {
  return `pos_org_tax_config_${userid}`;
};

export const getCartFromOrder = (order = {}) => {
  const items = Array.isArray(order?.items)
    ? order.items
    : [];

  return items.map((item) => ({
    item_id: item?.item_id || item?.product_id,
    product_id: item?.product_id || item?.item_id,

    item_name:
      item?.item_name ||
      item?.actualName ||
      "Item",

    qty: Number(
      item?.quantity ||
        item?.qty ||
        1,
    ),

    quantity: Number(
      item?.quantity ||
        item?.qty ||
        1,
    ),

    price: Number(
      item?.item_price ||
        item?.price ||
        0,
    ),

    item_price: Number(
      item?.item_price ||
        item?.price ||
        0,
    ),

    total_price: Number(
      item?.total_price ||
        0,
    ),

    image:
      item?.itemImage ||
      item?.image ||
      "",

    size:
      item?.sizeWords ||
      item?.size ||
      "Default",

    sizeWords:
      item?.sizeWords ||
      item?.size ||
      "Default",

    size_id: item?.size_id || "",

    customization:
      item?.customization ||
      "[]",
  }));
};